/**
 * Inspector section for a `BUTTON` widget — its whole configuration.
 *
 * A Feature dropdown at the top picks the button's ONE feature (download,
 * call API, filter, navigate, popup); only that feature's settings are shown
 * below it, after the label & style group every button has. Flat settings go
 * through the generic `DesignControlRendererComponent`; the parts that cannot
 * be a flat control (the API list, target checklists, request bodies) are
 * hand-written here. Everything is written to `widget.appearance`.
 */

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { NgSelectModule } from '@ng-select/ng-select';
import { SsSelectTooltipDirective } from '../../../shared/ss-select-tooltip.directive';
import { SelfServiceDatasetService } from '../../../shared/self-service-dataset.service';

import type { FormatControl, LabelValueOption } from '../../../shared/self-service-config.types';
import {
  BUTTON_ACTION_OPTIONS,
  BUTTON_DEFAULTS as D,
  buttonConfigOf,
  newButtonApiCall,
  type ButtonApiCall,
  type ButtonConfig,
  type ButtonSaveColumn,
} from '../../../../global-components/dvm-action-button/dvm-action-button.types';
import { DesignControlRendererComponent } from './design-control-renderer.component';

/** Local option lists, merged over the builder config's own `options`. */
// Font weights are numbers (the stored value), so this is wider than LabelValueOption.
const BUTTON_OPTIONS: Record<string, Array<{ label: string; value: string | number }>> = {
  buttonActions: BUTTON_ACTION_OPTIONS.map(({ label, value }) => ({ label, value })),
  buttonIconPositions: [
    { label: 'Left of label', value: 'left' },
    { label: 'Right of label', value: 'right' },
  ],
  buttonLabelAligns: [
    { label: 'Center', value: 'center' },
    { label: 'Left', value: 'left' },
    { label: 'Right', value: 'right' },
  ],
  buttonFontWeights: [300, 400, 500, 600, 700, 800].map((weight) => ({ label: String(weight), value: weight })),
  buttonMethods: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((method) => ({ label: method, value: method })),
  buttonBodyMethods: ['GET', 'POST', 'PUT', 'PATCH'].map((method) => ({ label: method, value: method })),
  buttonDownloadSources: [
    { label: 'Data of a widget', value: 'widget' },
    { label: 'Excel export (filtered rows)', value: 'excel' },
    { label: 'File from an API', value: 'api' },
  ],
  buttonDownloadFormats: [
    { label: 'CSV', value: 'csv' },
    { label: 'JSON', value: 'json' },
  ],
  buttonApiRunModes: [
    { label: 'One after another', value: 'sequential' },
    { label: 'All at once', value: 'parallel' },
  ],
  buttonSaveSources: [
    { label: 'Edited table rows', value: 'table' },
    { label: 'Values on the dashboard (business keys + columns)', value: 'values' },
  ],
  buttonUploadModes: [
    { label: 'Entity + business keys (file as bytes)', value: 'json' },
    { label: 'Form data (multipart)', value: 'multipart' },
  ],
  buttonApprovalRows: [
    { label: 'Only rows I edited', value: 'edited' },
    { label: 'Every row in the table', value: 'all' },
  ],
  buttonNavigateTypes: [
    { label: 'Landing page', value: 'landing' },
    { label: 'Published dashboard', value: 'dashboard' },
    { label: 'Screen in this design', value: 'screen' },
    { label: 'App route', value: 'route' },
    { label: 'External URL', value: 'url' },
  ],
  buttonPopupSources: [
    { label: 'A screen I design', value: 'screen' },
    { label: 'Write it here', value: 'inline' },
    { label: 'A Popup widget', value: 'widget' },
  ],
  buttonPopupSizes: [
    { label: 'Small', value: 'small' },
    { label: 'Medium', value: 'medium' },
    { label: 'Large', value: 'large' },
    { label: 'Square box', value: 'square' },
  ],
  buttonPopupHeadingSizes: [
    { label: 'Large', value: 'h1' },
    { label: 'Medium', value: 'h2' },
    { label: 'Small', value: 'h3' },
  ],
  buttonPopupVerticalAligns: [
    { label: 'Top', value: 'top' },
    { label: 'Middle', value: 'middle' },
    { label: 'Bottom', value: 'bottom' },
  ],
  buttonPopupPositions: [
    { label: 'Centered dialog', value: 'center' },
    { label: 'Side drawer', value: 'drawer' },
  ],
};

const FEATURE: FormatControl = { key: 'action', label: 'Feature', control: 'select', optionsKey: 'buttonActions', default: D.action };

const STYLE_CONTROLS: FormatControl[] = [
  { key: 'label', label: 'Label', control: 'text', default: D.label },
  { key: 'labelAlign', label: 'Align', control: 'select', optionsKey: 'buttonLabelAligns', default: D.labelAlign },
  { key: 'icon', label: 'Icon', control: 'text', default: D.icon, placeholder: 'Material icon, e.g. download' },
  { key: 'iconPosition', label: 'Icon side', control: 'select', optionsKey: 'buttonIconPositions', default: D.iconPosition },
  { key: 'fontFamily', label: 'Font', control: 'select', optionsKey: 'fontFamilies', default: D.fontFamily },
  { key: 'fontSize', label: 'Font size', control: 'number', default: D.fontSize, min: 8, max: 48 },
  { key: 'fontWeight', label: 'Weight', control: 'select', optionsKey: 'buttonFontWeights', default: D.fontWeight },
  { key: 'textColor', label: 'Label color', control: 'color', default: D.textColor },
  { key: 'backgroundColor', label: 'Fill', control: 'color', default: D.backgroundColor },
  { key: 'hoverBackgroundColor', label: 'Hover fill', control: 'color', default: D.hoverBackgroundColor },
  { key: 'borderColor', label: 'Border', control: 'color', default: D.borderColor },
  { key: 'borderWidth', label: 'Border width', control: 'number', default: D.borderWidth, min: 0, max: 8 },
  { key: 'borderRadius', label: 'Radius', control: 'number', default: D.borderRadius, min: 0, max: 64 },
  { key: 'fullWidth', label: 'Fill the box width', control: 'toggle', default: D.fullWidth },
];

const CONFIRM: FormatControl = {
  key: 'confirmMessage', label: 'Confirm', control: 'text', default: D.confirmMessage, placeholder: 'Ask before running (optional)',
};

/** Per-feature flat controls; the hand-written parts sit around them in the template. */
const FEATURE_CONTROLS: Record<string, FormatControl[]> = {
  downloadSource: [
    { key: 'downloadSource', label: 'Source', control: 'select', optionsKey: 'buttonDownloadSources', default: D.downloadSource },
  ],
  downloadWidget: [
    { key: 'downloadWidgetId', label: 'Widget', control: 'select', optionsKey: 'buttonWidgets', default: '' },
    { key: 'downloadFormat', label: 'Format', control: 'select', optionsKey: 'buttonDownloadFormats', default: D.downloadFormat },
  ],
  downloadExcel: [
    { key: 'downloadWidgetId', label: 'Table / widget', control: 'select', optionsKey: 'buttonWidgets', default: '' },
    { key: 'downloadEntity', label: 'Dataset (optional)', control: 'text', default: '', placeholder: 'Blank = the widget\'s dataset' },
    { key: 'downloadSheetName', label: 'Sheet name', control: 'text', default: D.downloadSheetName },
  ],
  downloadApi: [
    { key: 'downloadMethod', label: 'Method', control: 'select', optionsKey: 'buttonMethods', default: D.downloadMethod },
    { key: 'downloadUrl', label: 'URL', control: 'text', default: '', placeholder: '/api/reports/export' },
  ],
  downloadName: [
    { key: 'downloadFileName', label: 'File name', control: 'text', default: D.downloadFileName },
  ],
  approval: [
    { key: 'approvalUrl', label: 'Approval API', control: 'text', default: '', placeholder: '/approval-level/<workflow>/submit' },
    { key: 'approvalSliderId', label: 'Values from', control: 'select', optionsKey: 'buttonSliders', default: '' },
    { key: 'approvalSuccessMessage', label: 'On success', control: 'text', default: D.approvalSuccessMessage, placeholder: 'Message under the button' },
    { key: 'approvalRefresh', label: 'Reload the dashboard data after approving', control: 'toggle', default: D.approvalRefresh },
    { key: 'approvalNavigate', label: 'Then navigate (after the API succeeds)', control: 'toggle', default: D.approvalNavigate },
  ],
  saveSource: [
    { key: 'saveSource', label: 'Save', control: 'select', optionsKey: 'buttonSaveSources', default: D.saveSource },
  ],
  upload: [
    { key: 'uploadUrl', label: 'Upload API URL', control: 'text', default: '', placeholder: '/spriced/platform (blank = the platform API)' },
    { key: 'uploadMode', label: 'Send as', control: 'select', optionsKey: 'buttonUploadModes', default: D.uploadMode },
  ],
  uploadMultipart: [
    { key: 'uploadFieldName', label: 'File field name', control: 'text', default: D.uploadFieldName, placeholder: 'file' },
  ],
  uploadCommon: [
    { key: 'uploadAccept', label: 'File types', control: 'text', default: D.uploadAccept, placeholder: '.xlsx,.csv (blank = any)' },
    { key: 'uploadMaxSizeMb', label: 'Max size (MB)', control: 'number', default: D.uploadMaxSizeMb },
    { key: 'uploadSuccessMessage', label: 'On success', control: 'text', default: D.uploadSuccessMessage, placeholder: 'Message under the button' },
    { key: 'uploadRefresh', label: 'Reload the dashboard data after uploading', control: 'toggle', default: D.uploadRefresh },
  ],
  saveTable: [
    { key: 'saveTableId', label: 'Table to save', control: 'select', optionsKey: 'buttonSaveTables', default: '' },
  ],
  saveValues: [
    { key: 'saveUrl', label: 'Save API URL', control: 'text', default: '', placeholder: '/api/…' },
    { key: 'saveSuccessMessage', label: 'On success', control: 'text', default: D.saveSuccessMessage, placeholder: 'Message under the button' },
  ],
  /** Export: the component, the API and the file - Filter columns and Body are rendered separately. */
  export: [
    { key: 'exportWidgetId', label: 'Component', control: 'select', optionsKey: 'buttonWidgets', default: '' },
    { key: 'exportUrl', label: 'URL', control: 'text', default: D.exportUrl, placeholder: D.exportUrl },
    { key: 'exportSheetName', label: 'Sheet name', control: 'text', default: D.exportSheetName },
    { key: 'exportFileName', label: 'File name', control: 'text', default: '', placeholder: 'Blank = the dataset name' },
  ],
  /** Shown when "Values from" is a table: one { uuid, value } item per row. */
  approvalTable: [
    { key: 'approvalUuidColumn', label: 'ID column', control: 'text', default: D.approvalUuidColumn, placeholder: 'uuid (can be a hidden column)' },
    { key: 'approvalValueColumn', label: 'Value column', control: 'text', default: '', placeholder: 'Column to send, e.g. set_price' },
    { key: 'approvalRows', label: 'Rows', control: 'select', optionsKey: 'buttonApprovalRows', default: D.approvalRows },
  ],
  apiRun: [
    { key: 'apiRunMode', label: 'Run', control: 'select', optionsKey: 'buttonApiRunModes', default: D.apiRunMode },
    { key: 'apiSuccessMessage', label: 'On success', control: 'text', default: D.apiSuccessMessage, placeholder: 'Message under the button' },
  ],
  filter: [
    { key: 'filterField', label: 'Field', control: 'text', default: '', placeholder: 'Column to filter, e.g. Region' },
    { key: 'filterValue', label: 'Target value', control: 'text', default: '', placeholder: 'e.g. North — comma-separate several' },
    { key: 'filterToggle', label: 'Click again to clear the filter', control: 'toggle', default: D.filterToggle },
  ],
  filterApi: [
    { key: 'filterApiMethod', label: 'Method', control: 'select', optionsKey: 'buttonBodyMethods', default: D.filterApiMethod },
    { key: 'filterApiUrl', label: 'URL', control: 'text', default: '', placeholder: 'Empty = dashboard compute API' },
  ],
  navigateType: [
    { key: 'navigateType', label: 'Go to', control: 'select', optionsKey: 'buttonNavigateTypes', default: D.navigateType },
  ],
  navigateScreen: [
    { key: 'navigateScreenId', label: 'Screen', control: 'select', optionsKey: 'buttonScreens', default: '' },
  ],
  navigateRoute: [
    { key: 'navigateTarget', label: 'Route', control: 'text', default: '', placeholder: '/dvm-landing-builder' },
  ],
  navigateUrl: [
    { key: 'navigateTarget', label: 'URL', control: 'text', default: '', placeholder: 'https://…' },
  ],
  navigateTab: [
    { key: 'navigateNewTab', label: 'Open in a new tab', control: 'toggle', default: D.navigateNewTab },
  ],
  navigateFilter: [
    { key: 'navigateFilterKey', label: 'Filter key', control: 'text', default: '', placeholder: 'Optional, e.g. Region' },
    { key: 'navigateFilterValue', label: 'Filter value', control: 'text', default: '', placeholder: 'Optional, e.g. North' },
    { key: 'navigateSendFilters', label: "Send this screen's selections (all dropdown filters)", control: 'toggle', default: D.navigateSendFilters },
  ],
  popupSource: [
    { key: 'popupSource', label: 'Content', control: 'select', optionsKey: 'buttonPopupSources', default: D.popupSource },
  ],
  popupScreen: [
    { key: 'popupScreenId', label: 'Screen', control: 'select', optionsKey: 'buttonScreens', default: '' },
  ],
  popupWidget: [
    { key: 'popupWidgetId', label: 'Popup', control: 'select', optionsKey: 'buttonPopups', default: '' },
  ],
  popupInline: [
    { key: 'popupTitle', label: 'Title', control: 'text', default: D.popupTitle },
    { key: 'popupSize', label: 'Size', control: 'select', optionsKey: 'buttonPopupSizes', default: D.popupSize },
    { key: 'popupPosition', label: 'Position', control: 'select', optionsKey: 'buttonPopupPositions', default: D.popupPosition },
    { key: 'popupCloseLabel', label: 'Close label', control: 'text', default: D.popupCloseLabel, placeholder: 'Empty = no close button' },
  ],
  popupSquare: [
    { key: 'popupBoxSize', label: 'Box size (px)', control: 'number', default: D.popupBoxSize, min: 120, max: 1200, step: 10 },
  ],
  popupHeading: [
    { key: 'popupHeading', label: 'Heading', control: 'text', default: D.popupHeading, placeholder: 'Optional, bold text at the top' },
    { key: 'popupHeadingSize', label: 'Heading size', control: 'select', optionsKey: 'buttonPopupHeadingSizes', default: D.popupHeadingSize },
  ],
  popupStyle: [
    { key: 'popupShowHeader', label: 'Show title bar', control: 'toggle', default: D.popupShowHeader },
    { key: 'popupTextAlign', label: 'Text align', control: 'select', optionsKey: 'buttonLabelAligns', default: D.popupTextAlign },
    { key: 'popupVerticalAlign', label: 'Vertical', control: 'select', optionsKey: 'buttonPopupVerticalAligns', default: D.popupVerticalAlign },
    { key: 'popupFontSize', label: 'Font size', control: 'number', default: D.popupFontSize, min: 8, max: 64 },
    { key: 'popupFontWeight', label: 'Weight', control: 'select', optionsKey: 'buttonFontWeights', default: D.popupFontWeight },
    { key: 'popupTextColor', label: 'Text color', control: 'color', default: D.popupTextColor },
    { key: 'popupBackgroundColor', label: 'Box fill', control: 'color', default: D.popupBackgroundColor },
    { key: 'popupHeaderBackground', label: 'Title bar fill', control: 'color', default: D.popupHeaderBackground },
    { key: 'popupHeaderTextColor', label: 'Title color', control: 'color', default: D.popupHeaderTextColor },
    { key: 'popupBorderColor', label: 'Border', control: 'color', default: D.popupBorderColor },
    { key: 'popupBorderWidth', label: 'Border width', control: 'number', default: D.popupBorderWidth, min: 0, max: 12 },
    { key: 'popupBorderRadius', label: 'Radius', control: 'number', default: D.popupBorderRadius, min: 0, max: 64 },
    { key: 'popupCloseBackground', label: 'Close fill', control: 'color', default: D.popupCloseBackground },
    { key: 'popupCloseTextColor', label: 'Close text', control: 'color', default: D.popupCloseTextColor },
  ],
};

const FEATURE_TITLES: Record<string, string> = {
  download: 'Download',
  api: 'API calls',
  filter: 'Filter',
  navigate: 'Navigation',
  popup: 'Popup',
  closePopup: 'Close popup',
  approval: 'Approval',
  export: 'Export',
  saveTable: 'Save changes',
  upload: 'Upload',
};

const PICK: LabelValueOption = { label: 'Select…', value: '' };

@Component({
  selector: 'app-design-button-settings',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, FormsModule, MatIconModule, MatTooltipModule, NgSelectModule, SsSelectTooltipDirective, DesignControlRendererComponent],
  templateUrl: './button-settings.component.html',
  styleUrl: './button-settings.component.scss',
})
export class ButtonSettingsComponent {
  /** The widget's `appearance`. */
  readonly appearance = input<Record<string, unknown>>({});
  /** `layout.options` (font families…). */
  readonly options = input<Record<string, unknown>>({});
  /** Generated charts / tables / bound texts, by componentId — filter & API targets. */
  readonly targetOptions = input<LabelValueOption[]>([]);
  /** Widgets whose data can be downloaded, by node id. */
  readonly widgetOptions = input<LabelValueOption[]>([]);
  /** Popup widgets on the canvas, by node id. */
  readonly popupOptions = input<LabelValueOption[]>([]);
  /** Screens of this design, by root frame id. */
  readonly screenOptions = input<LabelValueOption[]>([]);
  /** Zone sliders on the page (the Approval feature's value source). */
  readonly sliderOptions = input<LabelValueOption[]>([]);
  /** Tables on the page - also an Approval value source (one item per row). */
  readonly approvalTableOptions = input<LabelValueOption[]>([]);
  /** A button that is not a widget (a table-cell button): its label / look are set elsewhere. */
  readonly hideStyle = input(false);
  /** Widget node id -> the dataset it reads (Export's Filter columns). */
  readonly widgetDatasets = input<Record<string, string>>({});

  private readonly datasetService = inject(SelfServiceDatasetService);
  /** Every column per dataset, uuid and system attributes included (filters address records). */
  private readonly exportColumnsByDataset = signal<Record<string, string[] | 'loading' | 'error'>>({});
  /** The dataset of the component Export reads. */
  readonly exportDataset = computed(() => this.widgetDatasets()[this.config().exportWidgetId] ?? '');
  readonly exportColumns = computed(() => {
    const state = this.exportColumnsByDataset()[this.exportDataset()];
    return Array.isArray(state) ? state : [];
  });
  readonly exportColumnsLoading = computed(() => this.exportColumnsByDataset()[this.exportDataset()] === 'loading');

  /** Value widgets on the canvas (input boxes, dropdowns, sliders) - "Save changes" columns read them. */
  readonly valueSourceOptions = input<LabelValueOption[]>([]);
  readonly valueSourceItems = computed(() => [{ label: 'Auto - the input / dropdown for this column', value: '' }, ...this.valueSourceOptions()]);

  /** "Save changes" from the dashboard's values. */
  readonly savesValues = computed(() => this.config().action === 'saveTable' && this.config().saveSource === 'values');
  private readonly datasetList = signal<string[] | 'loading' | 'error' | null>(null);
  readonly datasets = computed(() => {
    const list = this.datasetList();
    return Array.isArray(list) ? list : [];
  });
  readonly datasetsLoading = computed(() => this.datasetList() === 'loading');
  readonly saveColumnsOfEntity = computed(() => {
    const state = this.exportColumnsByDataset()[this.config().saveEntity.trim()];
    return Array.isArray(state) ? state : [];
  });
  readonly saveColumnsLoading = computed(() => this.exportColumnsByDataset()[this.config().saveEntity.trim()] === 'loading');
  /** Upload: every dataset (the Entity dropdown), loaded once. */
  readonly uploadEntities = signal<string[]>([]);
  readonly uploadEntitiesLoading = signal(false);
  readonly uploadColumns = computed(() => {
    const state = this.exportColumnsByDataset()[this.config().uploadEntity];
    return Array.isArray(state) ? state : [];
  });
  readonly uploadColumnsLoading = computed(() => this.exportColumnsByDataset()[this.config().uploadEntity] === 'loading');

  constructor() {
    effect(() => {
      if (this.config().action !== 'upload' || this.config().uploadMode === 'multipart') return;
      const entity = this.config().uploadEntity;
      untracked(() => {
        if (!this.uploadEntities().length && !this.uploadEntitiesLoading()) {
          this.uploadEntitiesLoading.set(true);
          this.datasetService.getEntities().subscribe({
            next: (entities) => { this.uploadEntities.set(entities); this.uploadEntitiesLoading.set(false); },
            error: () => this.uploadEntitiesLoading.set(false),
          });
        }
        if (entity) this.loadColumns(entity);
      });
    });
    // Load the picked component's columns once per dataset.
    effect(() => {
      const dataset = this.exportDataset();
      if (dataset && this.config().action === 'export') untracked(() => this.loadColumns(dataset));
    });
    effect(() => {
      if (!this.savesValues()) return;
      const entity = this.config().saveEntity.trim();
      untracked(() => {
        if (entity) this.loadColumns(entity);
        if (this.datasetList() !== null) return;
        this.datasetList.set('loading');
        this.datasetService.getEntities().subscribe({
          next: (names) => this.datasetList.set([...new Set(names)]),
          error: () => this.datasetList.set('error'),
        });
      });
    });
  }

  /** A dataset's columns (uuid and system attributes included), fetched once. */
  private loadColumns(dataset: string): void {
    if (this.exportColumnsByDataset()[dataset]) return;
    this.exportColumnsByDataset.update((all) => ({ ...all, [dataset]: 'loading' }));
    this.datasetService.getEntityAttributes(dataset).subscribe({
      next: (names) => this.exportColumnsByDataset.update((all) => ({ ...all, [dataset]: [...new Set(names)].filter((name) => name !== '__rowKey') })),
      error: () => this.exportColumnsByDataset.update((all) => ({ ...all, [dataset]: 'error' })),
    });
  }

  // ── Save changes: the columns to update ────────────────────────────────────

  readonly saveColumnNames = computed(() => this.config().saveColumns.map((entry) => entry.column).filter(Boolean));

  /** The columns picked in "Columns to update": kept ones keep their "Value from", new ones start on Auto. */
  setSaveColumnNames(names: string[]): void {
    const current = new Map(this.config().saveColumns.map((entry) => [entry.column, entry]));
    this.set('saveColumns', [...new Set(names.map(String).filter(Boolean))].map((column) => current.get(column) ?? { column, sourceId: '' }));
  }

  setSaveColumn(index: number, patch: Partial<ButtonSaveColumn>): void {
    this.set('saveColumns', this.config().saveColumns.map((entry, i) => (i === index ? { ...entry, ...patch } : entry)));
  }

  removeSaveColumn(index: number): void {
    this.set('saveColumns', this.config().saveColumns.filter((_, i) => i !== index));
  }
  /** Saved dashboards (slug) a button can navigate to. */
  readonly dashboardOptions = input<LabelValueOption[]>([]);
  /** A partial appearance patch — the inspector merges it. */
  readonly appearanceChange = output<Record<string, unknown>>();
  /** "Create popup screen" — the inspector adds the frame and links it. */
  readonly createPopupScreen = output<void>();
  /** Whether this button's popup screen exists, and if it is on the canvas. */
  readonly popupScreenState = input<'none' | 'shown' | 'hidden'>('none');
  /** "Update popup screen" — show the frame again for edits. */
  readonly openPopupScreen = output<void>();
  /** "Done" — hide the frame from the canvas. */
  readonly hidePopupScreen = output<void>();

  readonly feature = FEATURE;
  readonly styleControls = STYLE_CONTROLS;
  readonly confirm = CONFIRM;
  readonly controls = FEATURE_CONTROLS;
  readonly actions = BUTTON_ACTION_OPTIONS;
  readonly methods = BUTTON_OPTIONS['buttonMethods'];

  readonly config = computed<ButtonConfig>(() => buttonConfigOf(this.appearance()));

  readonly allOptions = computed<Record<string, unknown>>(() => ({
    ...this.options(),
    ...BUTTON_OPTIONS,
    buttonWidgets: [PICK, ...this.widgetOptions()],
    buttonPopups: [PICK, ...this.popupOptions()],
    buttonScreens: [PICK, ...this.screenOptions()],
    buttonSliders: [{ label: 'First slider on the page', value: '' }, ...this.sliderOptions(), ...this.approvalTableOptions()],
    buttonSaveTables: [{ label: 'First table on the screen', value: '' }, ...this.approvalTableOptions()],
  }));

  /** "Values from" is a table: show the ID / Value column and Rows settings. */
  readonly approvalFromTable = computed(() => {
    const source = this.config().approvalSliderId;
    const tables = this.approvalTableOptions();
    // Left on the default with no slider on the page, the runtime uses the first table.
    if (!source) return !this.sliderOptions().length && tables.length > 0;
    return tables.some((option) => option.value === source);
  });

  readonly featureTitle = computed(() => FEATURE_TITLES[this.config().action] ?? 'Feature');

  readonly actionIcon = computed(() => this.actions.find((action) => action.value === this.config().action)?.icon ?? 'smart_button');

  private readonly openGroups = signal<ReadonlySet<string>>(new Set(['feature']));

  isOpen(group: string): boolean {
    return this.openGroups().has(group);
  }

  toggle(group: string): void {
    this.openGroups.update((current) => {
      const next = new Set(current);
      if (!next.delete(group)) next.add(group);
      return next;
    });
  }

  onControlChange(change: { key: string; value: unknown }): void {
    this.appearanceChange.emit({ [change.key]: change.value });
  }

  set(key: keyof ButtonConfig, value: unknown): void {
    this.appearanceChange.emit({ [key]: value });
  }

  // ── Target checklists ──────────────────────────────────────────────────────

  isTarget(key: 'filterTargets' | 'apiTargets', componentId: string): boolean {
    return this.config()[key].includes(componentId);
  }

  toggleTarget(key: 'filterTargets' | 'apiTargets', componentId: string, checked: boolean): void {
    const rest = this.config()[key].filter((id) => id !== componentId);
    this.set(key, checked ? [...rest, componentId] : rest);
  }

  // ── API list ───────────────────────────────────────────────────────────────

  addApi(): void {
    const apis = this.config().apis;
    this.set('apis', [...apis, newButtonApiCall(apis.length)]);
  }

  setApi(index: number, patch: Partial<ButtonApiCall>): void {
    this.set('apis', this.config().apis.map((api, i) => (i === index ? { ...api, ...patch } : api)));
  }

  removeApi(index: number): void {
    this.set('apis', this.config().apis.filter((_, i) => i !== index));
  }

  moveApi(index: number, delta: -1 | 1): void {
    const next = [...this.config().apis];
    const to = index + delta;
    if (to < 0 || to >= next.length) return;
    [next[index], next[to]] = [next[to], next[index]];
    this.set('apis', next);
  }

  hasBody(method: string): boolean {
    return method !== 'GET' && method !== 'DELETE';
  }

  /** A JSON text field that does not parse — flagged, but still saved as typed. */
  isBadJson(text: string, allowTokens = true): boolean {
    const raw = String(text ?? '').trim();
    if (!raw) return false;
    // Unquoted `{token}`s are valid templates; quote them for the check.
    const probe = allowTokens ? raw.replace(/(?<!")\{(\w+)\}(?!")/g, '"$1"') : raw;
    try {
      JSON.parse(probe);
      return false;
    } catch {
      return true;
    }
  }

  trackByControlKey(_: number, control: FormatControl): string {
    return control.key;
  }

  trackByValue(_: number, option: { value: unknown }): string {
    return String(option.value);
  }

  trackByApi(_: number, api: ButtonApiCall): string {
    return api.id;
  }
}
